import { Box } from '@mui/material';
import { barGeometry, type TimelineWindow } from './timeline';
import type { PlanItem } from './vehiclePlanApi';
import {
  BRACKET_COLORS,
  BRACKET_LINE_PX,
  bracketPlacement,
  bracketTestId,
  bracketTip,
  checkBracket,
  drawableBrackets,
  type WindowBracket,
} from './bracketRules';

/** Pozycja z nawiasami — pozycja planu albo podgląd rekomendowanego zlecenia. */
type BracketItem = Pick<PlanItem, 'orderId' | 'number' | 'segments' | 'overrunUntil' | 'windowBrackets'>;

/**
 * Nawiasy okien klienta Z/R przy pasku pozycji (24.10.2026, S-24.10-05,
 * przełącznik `ganttWindowBrackets`; reguły — `bracketRules.ts`).
 *
 * Nawias Z stoi u góry wiersza paska (haczyki nad paskiem), nawias R — u dołu
 * (haczyki pod paskiem), więc oba widać także wtedy, gdy obejmują ten sam
 * przedział (jedno okno klienta). Pionowe kreski wyznaczają granice okna;
 * część schowana pod paskiem nic nie traci, bo pasek leży w oknie. Okno
 * ucięte krawędzią osi — nawias otwarty z tej strony ze strzałką; okno całe
 * poza osią — strzałka z nazwą przy krawędzi. Kolizja — czerwony nawias.
 *
 * Tylko odczyt: bez przeciągania i bez zdarzeń myszy poza chmurką.
 */
export const WindowBrackets = ({
  item,
  window,
  barTop,
  barHeight,
  hook,
  tipAttr,
  labels,
}: {
  item: BracketItem;
  window: TimelineWindow;
  /** Góra paska w torze (px). */
  barTop: number;
  barHeight: number;
  /** O ile haczyk nawiasu stoi nad / pod paskiem (px). */
  hook: number;
  /** Jak chmurka: `data-tip` (karta pojazdu, `TipLayer`) albo `title` (harmonogram floty). */
  tipAttr: 'data-tip' | 'title';
  /** Nazwa nawiasu (Z1, R1…) przy haczyku — gdy wiersz ma na nią miejsce. */
  labels: boolean;
}) => (
  <>
    {drawableBrackets(item).map((b) => (
      <Bracket
        key={`${b.kind}-${b.index}`}
        item={item}
        b={b}
        window={window}
        barTop={barTop}
        barHeight={barHeight}
        hook={hook}
        tipAttr={tipAttr}
        labels={labels}
      />
    ))}
  </>
);

const Bracket = ({
  item,
  b,
  window,
  barTop,
  barHeight,
  hook,
  tipAttr,
  labels,
}: {
  item: BracketItem;
  b: WindowBracket;
  window: TimelineWindow;
  barTop: number;
  barHeight: number;
  hook: number;
  tipAttr: 'data-tip' | 'title';
  labels: boolean;
}) => {
  const check = checkBracket(item, b);
  const color = check.conflict ? BRACKET_COLORS.conflict : BRACKET_COLORS[b.kind];
  const tip = bracketTip(item, b);
  const top = b.kind === 'loading' ? barTop - hook : barTop + barHeight / 2;
  const height = barHeight / 2 + hook;
  const line = `${BRACKET_LINE_PX}px solid ${color}`;
  const common = {
    'data-testid': bracketTestId(b),
    'data-order': item.orderId,
    'data-kind': b.kind,
    'data-conflict': check.conflict ? 'true' : undefined,
    [tipAttr]: tip,
    role: 'img',
    'aria-label': tip.split('\n').slice(0, 2).join(', '),
    draggable: false,
  };
  const labelSx = {
    position: 'absolute' as const,
    px: '2px',
    fontSize: '0.6rem',
    fontWeight: 700,
    lineHeight: '10px',
    color,
    bgcolor: 'background.paper',
    whiteSpace: 'nowrap' as const,
    pointerEvents: 'none' as const,
  };

  const placement = bracketPlacement(b, window);
  if (placement !== 'inView') {
    // Okno całe poza osią — nawias otwarty: strzałka z nazwą przy krawędzi.
    const before = placement === 'before';
    return (
      <Box
        {...common}
        data-open={before ? 'before' : 'after'}
        sx={{
          position: 'absolute',
          top: b.kind === 'loading' ? top - 5 : top + height - 5,
          [before ? 'left' : 'right']: 2,
          zIndex: 3,
          px: '3px',
          fontSize: '0.6rem',
          fontWeight: 700,
          lineHeight: '10px',
          color,
          bgcolor: 'background.paper',
          border: `1px solid ${color}`,
          borderRadius: '2px',
          whiteSpace: 'nowrap',
          cursor: 'default',
        }}
      >
        {before ? `‹ ${b.label}` : `${b.label} ›`}
      </Box>
    );
  }

  const g = barGeometry(b.from, b.to, window, 0);
  if (!g) return null;
  const open = g.clippedStart && g.clippedEnd ? 'both' : g.clippedStart ? 'start' : g.clippedEnd ? 'end' : undefined;
  return (
    <Box
      {...common}
      data-open={open}
      sx={{
        position: 'absolute',
        left: `${g.left}%`,
        width: `${g.width}%`,
        minWidth: 4,
        top,
        height,
        boxSizing: 'border-box',
        zIndex: 0,
        cursor: 'default',
        borderLeft: g.clippedStart ? 'none' : line,
        borderRight: g.clippedEnd ? 'none' : line,
        ...(b.kind === 'loading' ? { borderTop: line } : { borderBottom: line }),
      }}
    >
      {g.clippedStart ? (
        <Box component="span" aria-hidden sx={{ ...labelSx, left: 0, [b.kind === 'loading' ? 'top' : 'bottom']: -5 }}>
          ‹
        </Box>
      ) : null}
      {g.clippedEnd ? (
        <Box component="span" aria-hidden sx={{ ...labelSx, right: 0, [b.kind === 'loading' ? 'top' : 'bottom']: -5 }}>
          ›
        </Box>
      ) : null}
      {labels ? (
        <Box
          component="span"
          aria-hidden
          sx={{
            ...labelSx,
            ...(b.kind === 'loading'
              ? { top: -5, left: g.clippedStart ? 10 : 3 }
              : { bottom: -5, right: g.clippedEnd ? 10 : 3 }),
          }}
        >
          {b.label}
        </Box>
      ) : null}
    </Box>
  );
};
